import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, FileText, Loader2, RotateCw, Sparkles } from "lucide-react";

import {
  type GrantOpportunityDocumentLink,
  type GrantOpportunityDocumentProcessingStatus,
  PACKAGE_FILE_ACCEPT,
  listGrantOpportunityDocuments,
  packageFileRejection,
  retryGrantOpportunityDocumentProcessing,
  uploadGrantOpportunityDocument,
} from "@/api/grant_opportunity_documents";
import { confirmPlan } from "@/api/grant_opportunity_plan_confirmations";
import {
  type MunicipalityCollaborator,
  listMunicipalityCollaborators,
} from "@/api/municipality_collaborators";
import {
  type PlanAnswer,
  type PlanProposal,
  type PlanProposalRun,
  continuePlanWithFiles,
  createPlanProposal,
  listPlanProposals,
  submitPlanAnswers,
} from "@/api/grant_opportunity_plan_proposals";
import {
  GrantPlanReviewEditor,
  PlanContextSections,
  type ReviewPlan,
  confirmationPayloadFromReviewPlan,
  reviewPlanFromProposal,
} from "@/components/grant_suite/GrantPlanReviewEditor";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCurrentUser } from "@/context/UserProvider";
import { userIsOutsideUser } from "@/lib/grantSuiteAccess";
import {
  grantOpportunityEventsQueryKey,
  invalidateGrantSuitePipeline,
} from "@/lib/grantSuiteQueryKeys";
import { cn } from "@/lib/utils";

// The shared AI Partner planning modal (upload through finalize is one
// modal — ENG-1827 starts it with the package upload step; ENG-1822 adds
// the plan step: draft with the agent, then review the proposal like a
// diff. ENG-1931 surfaces a needs_input run's plan-mode questions in the
// plan step and continues the run from the answers. ENG-1950 wires
// Confirm to ENG-1823's persistence endpoint: the reviewed plan becomes
// real milestones, tasks, required documents, and requirements, and the
// modal closes onto the opportunity).
type PlanningStep = "package" | "plan";

type GrantPlanningModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  municipalityId: number;
  opportunityId: number;
  // Owner options on a department-scoped opportunity narrow to department
  // members (ENG-1824), same as the confirmed milestones UI.
  departmentId?: number | null;
  opportunityTitle: string;
};

// A file the user just picked, before the server knows about it. Rejected
// files stay visible with their reason; a failed upload keeps the File so
// Retry can resend exactly that file without touching any other row.
type PendingPackageFile = {
  id: number;
  file: File;
  status: "queued" | "uploading" | "failed";
  error: string | null;
  canRetry: boolean;
};

const SERVER_STATUS_LABELS: Record<GrantOpportunityDocumentProcessingStatus, string> = {
  queued: "Queued",
  uploading: "Uploading",
  parsing: "Parsing",
  ready: "Ready",
  failed: "Failed",
  retrying: "Retrying",
};

export function GrantPlanningModal({
  open,
  onOpenChange,
  municipalityId,
  opportunityId,
  departmentId = null,
  opportunityTitle,
}: GrantPlanningModalProps) {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const [step, setStep] = useState<PlanningStep>("package");
  const [pendingFiles, setPendingFiles] = useState<PendingPackageFile[]>([]);
  // The reviewable plan, seeded from the latest completed draft run and then
  // owned by the user's edits. seededRunId keeps a poll refetch from
  // clobbering those edits: only a *new* completed run reseeds.
  const [reviewPlan, setReviewPlan] = useState<ReviewPlan | null>(null);
  const [seededRunId, setSeededRunId] = useState<number | null>(null);
  const [draftPending, setDraftPending] = useState(false);
  // An enqueue that never made a run row (403, network) has no server state
  // for the poll to surface, so it must be shown from here or the Draft
  // button just appears to do nothing.
  const [draftError, setDraftError] = useState<string | null>(null);
  // Same split for the answers round-trip (ENG-1931): pending keeps the
  // spinner up between the POST and the cache seed that surfaces the
  // continuation run; the error covers a POST that made no run at all.
  const [answersPending, setAnswersPending] = useState(false);
  const [answersError, setAnswersError] = useState<string | null>(null);
  // Typed answers live here, not in the questions panel, so they survive
  // the plan step unmounting (Back to files, a drafting spinner). They
  // reset only when the opportunity changes or a new round of questions
  // arrives — seenParkedRunId tracks which round they belong to.
  const [answerTexts, setAnswerTexts] = useState<Record<number, string>>({});
  const [seenParkedRunId, setSeenParkedRunId] = useState<number | null>(null);
  // Confirm's round-trip state (ENG-1950): pending holds the button through
  // the POST so a double click cannot write the plan twice; the error is
  // shown beside the footer because a failed confirm leaves the user in
  // review to fix or retry.
  const [confirmPending, setConfirmPending] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  // The modal is one persistent instance under the tracking panel's detail
  // view, so switching the selected opportunity must not carry another
  // opportunity's step, edits, or errors along (they would be confirmed
  // into the wrong opportunity).
  const [planScopeId, setPlanScopeId] = useState(opportunityId);
  if (planScopeId !== opportunityId) {
    setPlanScopeId(opportunityId);
    setStep("package");
    setReviewPlan(null);
    setSeededRunId(null);
    setDraftError(null);
    setDraftPending(false);
    setAnswersError(null);
    setAnswersPending(false);
    setAnswerTexts({});
    setSeenParkedRunId(null);
    setConfirmError(null);
    setConfirmPending(false);
    setPendingFiles([]);
  }
  const nextRowId = useRef(0);
  // Same refresh-every-render pattern as openRef below: startDraft's async
  // tail must know whether the modal still points at the opportunity it
  // enqueued for, or its writes would resurrect state the reset cleared.
  const scopeRef = useRef(opportunityId);
  scopeRef.current = opportunityId;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Refreshed every render so uploads that settle after a close can tell the
  // modal is no longer open and discard their row instead of updating it.
  const openRef = useRef(open);
  openRef.current = open;

  const { data: documents = [] } = useQuery({
    queryKey: ["grant_opportunity_documents", municipalityId, opportunityId],
    queryFn: () => listGrantOpportunityDocuments(municipalityId, opportunityId),
    enabled: open,
    // Poll while the parse pipeline holds any file in a non-terminal state,
    // so "Parsing" resolves to "Ready"/"Failed" without closing the modal.
    refetchInterval: (query) =>
      (query.state.data ?? []).some(
        (link) =>
          link.role === "package_source" && !["ready", "failed"].includes(link.processing_status),
      )
        ? 4000
        : false,
  });
  const packageFiles = documents.filter((link) => link.role === "package_source");
  const readyFileCount = packageFiles.filter((link) => link.processing_status === "ready").length;

  // Owner options for the review editor's milestone selector (ENG-1981).
  // Same query key as GrantOpportunityTasks so the two surfaces share the
  // cache; same outside-user gate, since the collaborators endpoint is
  // denied to them (they cannot reach this modal anyway).
  const outsideUser = userIsOutsideUser(currentUser);
  const { data: collaborators = [], isLoading: collaboratorsLoading } = useQuery({
    queryKey: ["municipality_collaborators", municipalityId, departmentId ?? null],
    queryFn: () => listMunicipalityCollaborators(municipalityId, departmentId),
    enabled: open && step === "plan" && !outsideUser,
  });

  const planRunsKey = (scopeId: number) => [
    "grant_opportunity_plan_proposals",
    municipalityId,
    scopeId,
  ];
  // Which completed run has already been confirmed into real records
  // (ENG-1950). Lives in the query cache, not component state, because the
  // opportunity-scope reset below wipes component state — a confirmed
  // opportunity revisited in the same session must not reseed the editor
  // and offer the same plan for a second confirm. This marker only bridges
  // the window until the next runs refetch: the durable guard is the run's
  // server-side confirmed_at, which the seeding condition also checks (and
  // the endpoint 409s a double confirm regardless).
  const confirmedRunKey = (scopeId: number) => [
    "grant_plan_confirmed_run",
    municipalityId,
    scopeId,
  ];

  const { data: planRuns = [], isPending: planRunsPending } = useQuery({
    queryKey: planRunsKey(opportunityId),
    queryFn: () => listPlanProposals(municipalityId, opportunityId),
    enabled: open && step === "plan",
    // Poll while a draft run is in flight so the agent's completion swaps
    // the spinner for the review editor without closing the modal.
    // retrying counts: it is the job's transient-error backoff, still the
    // same run — needs_input does not, a parked run stays parked until
    // the user acts.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((run) =>
        ["pending", "processing", "retrying"].includes(run.status),
      )
        ? 3000
        : false,
  });
  // Newest first from the API; the head row is the run the plan step is
  // "about" (in flight, failed, parked on questions, or the draft under
  // review).
  const latestRun: PlanProposalRun | undefined = planRuns[0];
  const latestCompleted = planRuns.find((run) => run.status === "completed");
  // answersPending stays out of drafting: the questions panel must remain
  // mounted through its own POST so a failure keeps the user's typed
  // answers. On success sendAnswers seeds the continuation into the cache
  // before pending clears, so drafting flips without a frame of stale
  // questions in between.
  const drafting =
    draftPending || ["pending", "processing", "retrying"].includes(latestRun?.status ?? "");
  const parkedRun = !drafting && latestRun?.status === "needs_input" ? latestRun : null;
  // Gate for the files-only continuation: a package file that finished
  // parsing after the run parked is new context worth re-drafting on;
  // without one the continuation would burn an agent run to re-read the
  // same files and park on the same questions.
  const filesAddedSincePark = parkedRun
    ? packageFiles.some(
        (link) =>
          link.processing_status === "ready" &&
          new Date(link.created_at) > new Date(parkedRun.created_at),
      )
    : false;

  // A newly parked run supersedes stale action errors the same way a
  // completed one does: the failed POST may have raced a run that was
  // actually enqueued and has now parked, and its questions must not stay
  // hidden behind that error. Keyed on the run id so a genuine failure
  // *while* parked (e.g. Draft a new plan 403s) still shows — only a new
  // round of questions clears, and it also starts the answers blank
  // (earlier rounds carry forward server-side).
  if (parkedRun && parkedRun.id !== seenParkedRunId) {
    setSeenParkedRunId(parkedRun.id);
    setDraftError(null);
    setAnswersError(null);
    setAnswerTexts({});
  }

  // Seed (or reseed) the review editor exactly once per completed run:
  // re-running the agent replaces the working copy, poll refetches never do,
  // and a run whose plan was already confirmed never does either — its
  // records exist, so reoffering it would double-create them.
  // (React's adjust-state-during-render pattern, not an effect, so the
  // editor renders seeded on the same pass the run arrives.)
  if (
    latestCompleted?.proposal &&
    !latestCompleted.confirmed_at &&
    seededRunId !== latestCompleted.id &&
    queryClient.getQueryData(confirmedRunKey(opportunityId)) !== latestCompleted.id
  ) {
    setSeededRunId(latestCompleted.id);
    setReviewPlan(reviewPlanFromProposal(latestCompleted.proposal));
    // A completed run supersedes any stale enqueue error (the failed POST
    // may have raced a draft that was already running and has now landed).
    setDraftError(null);
    setAnswersError(null);
  }

  // One choreography for every plan-step enqueue action (draft, answers —
  // confirm is not one: it persists records instead of enqueueing a run,
  // so it has its own handler below). The invariants the tests pin live here so
  // each action cannot drift from them: pending/error state is dropped if
  // the modal has moved to another opportunity; the 202 run is seeded
  // straight into the cache so polling resumes even when the follow-up
  // refetch fails; and the invalidate is fire-and-forget so a flaky refetch
  // cannot hold the action's button disabled through its retry backoff. An
  // action may resolve null to mean "nothing enqueued" (it already
  // reconciled the cache itself).
  const runPlanAction = async (
    action: () => Promise<PlanProposalRun | null>,
    setPending: (pending: boolean) => void,
    setError: (error: string | null) => void,
    fallbackMessage: string,
  ) => {
    const scopeId = opportunityId;
    setPending(true);
    setError(null);
    try {
      const run = await action();
      if (run && typeof run.id === "number") {
        queryClient.setQueryData<PlanProposalRun[]>(planRunsKey(scopeId), (runs = []) => [
          run,
          ...runs.filter((other) => other.id !== run.id),
        ]);
      }
    } catch (error) {
      // Dropped when the modal has moved to another opportunity: this is
      // the old opportunity's failure, and the scope reset already cleared
      // the plan step for the new one.
      if (scopeRef.current === scopeId) {
        setError(error instanceof Error ? error.message : fallbackMessage);
      }
    } finally {
      // A failed enqueue may still have raced a run that is now in flight;
      // the refetch surfaces it without holding this action hostage.
      void queryClient.invalidateQueries({ queryKey: planRunsKey(scopeId) });
      if (scopeRef.current === scopeId) {
        setPending(false);
      }
    }
  };

  const startDraft = () =>
    runPlanAction(
      () => createPlanProposal(municipalityId, opportunityId),
      setDraftPending,
      setDraftError,
      "Failed to start plan draft",
    );

  // Reconfirms the parked run is still the head before posting: a stale tab
  // (needs_input never polls) would otherwise enqueue a duplicate
  // continuation — or hit the dedupe hole where an unrelated in-flight run
  // swallows the answers entirely (ENG-1934). Shared by both continuation
  // shapes (typed answers, files-only) so the precheck cannot drift.
  const continueParkedRun = (runId: number, post: () => Promise<PlanProposalRun>) =>
    runPlanAction(
      async () => {
        const scopeId = opportunityId;
        const fresh = await listPlanProposals(municipalityId, scopeId);
        const head = fresh[0];
        if (!head || head.id !== runId || head.status !== "needs_input") {
          // The questions were already answered elsewhere (or superseded by
          // a new draft): show that truth instead of posting against it.
          queryClient.setQueryData(planRunsKey(scopeId), fresh);
          if (head && head.status === "needs_input") {
            // The truth is a *different* round of questions. Silently
            // swapping them would eat what the user typed with no trace, so
            // say what happened. Registering the round here (instead of
            // letting the render-phase block do it) keeps this error
            // visible — that block clears action errors when a new round
            // arrives — while still blanking the typed answers, which
            // belong to questions that no longer exist.
            setSeenParkedRunId(head.id);
            setAnswerTexts({});
            throw new Error(
              "AI Partner already received answers elsewhere and asked new questions. Your typed answers were not sent.",
            );
          }
          return null;
        }
        return post();
      },
      setAnswersPending,
      setAnswersError,
      "Failed to continue the draft",
    );

  const sendAnswers = (runId: number, answers: PlanAnswer[]) =>
    continueParkedRun(runId, () => submitPlanAnswers(municipalityId, opportunityId, runId, answers));

  // The questions panel's other path: the user added package files instead
  // of typing answers, and the agent re-drafts with the new files plus the
  // still-open questions in context (files_only continuation, ENG-1934).
  const continueWithFiles = (runId: number) =>
    continueParkedRun(runId, () => continuePlanWithFiles(municipalityId, opportunityId, runId));

  // Confirm persists the reviewed plan (ENG-1950 → ENG-1823's endpoint) and
  // closes the modal onto the opportunity, whose panels are invalidated so
  // the new work is already visible when the dialog is gone. On success the
  // review copy is dropped but seededRunId is kept: the completed run must
  // not reseed the editor on reopen and offer the same plan for a second
  // confirm. A failure keeps the user in review with the reason.
  const confirmReviewedPlan = async () => {
    if (!reviewPlan) return;
    const scopeId = opportunityId;
    setConfirmPending(true);
    setConfirmError(null);
    try {
      await confirmPlan(municipalityId, scopeId, {
        ...confirmationPayloadFromReviewPlan(reviewPlan),
        // Ties the confirm to the reviewed run so the server can stamp it
        // confirmed and refuse a duplicate from a reload or another tab.
        plan_proposal_id: seededRunId,
      });
      // Mark the seeded run consumed before any state resets: the seeding
      // guard reads this, so the run cannot reoffer its plan after the
      // scope reset (or anything else) clears seededRunId.
      queryClient.setQueryData(confirmedRunKey(scopeId), seededRunId);
      void queryClient.invalidateQueries({
        queryKey: ["grant_opportunity_milestones", municipalityId, scopeId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["grant_opportunity_tasks", municipalityId, scopeId],
      });
      void queryClient.invalidateQueries({
        queryKey: grantOpportunityEventsQueryKey(municipalityId, scopeId),
      });
      // The confirm may have replaced the AI Partner placeholder title; the
      // pipeline list is where that shows.
      invalidateGrantSuitePipeline(queryClient, municipalityId);
      if (scopeRef.current !== scopeId) return;
      setReviewPlan(null);
      setStep("package");
      handleOpenChange(false);
    } catch (error) {
      if (scopeRef.current === scopeId) {
        setConfirmError(error instanceof Error ? error.message : "Failed to confirm the plan");
      }
    } finally {
      if (scopeRef.current === scopeId) {
        setConfirmPending(false);
      }
    }
  };

  const updateRow = (id: number, patch: Partial<PendingPackageFile>) => {
    setPendingFiles((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const invalidateDocuments = () =>
    queryClient.invalidateQueries({
      queryKey: ["grant_opportunity_documents", municipalityId, opportunityId],
    });

  // Requeue a server-side parse failure (ENG-1826). Errors are not surfaced
  // here: the refetch shows the authoritative row state either way, and a
  // race (the file already retrying) resolves to the truth on its own.
  const retryServerFile = async (link: GrantOpportunityDocumentLink) => {
    try {
      await retryGrantOpportunityDocumentProcessing(municipalityId, opportunityId, link.id);
    } catch {
      // Refetch below is the recovery.
    }
    await invalidateDocuments();
  };

  const uploadRow = async (row: PendingPackageFile) => {
    updateRow(row.id, { status: "uploading", error: null });
    try {
      await uploadGrantOpportunityDocument(municipalityId, opportunityId, {
        file: row.file,
        role: "package_source",
      });
      // Refetch before dropping the pending row: the server row (with its
      // processing_status) must already be in the list when this one leaves,
      // or a finished file vanishes until slower siblings settle.
      await invalidateDocuments();
      setPendingFiles((rows) => rows.filter((other) => other.id !== row.id));
    } catch (error) {
      // A failure that lands after close would resurface as a stale failed
      // row on reopen — the row was only kept alive to finish, so drop it.
      if (!openRef.current) {
        setPendingFiles((rows) => rows.filter((other) => other.id !== row.id));
        return;
      }
      updateRow(row.id, {
        status: "failed",
        error: error instanceof Error ? error.message : "Upload failed",
        canRetry: true,
      });
    }
  };

  const handleFilesSelected = async (fileList: FileList | null) => {
    const files = Array.from(fileList ?? []);
    if (files.length === 0) return;
    // Allow re-picking the same file name after a fix.
    if (fileInputRef.current) fileInputRef.current.value = "";

    const rows = files.map((file): PendingPackageFile => {
      const rejection = packageFileRejection(file);
      return {
        id: nextRowId.current++,
        file,
        status: rejection ? "failed" : "queued",
        // A rejected file can't succeed by resending, so it gets the reason
        // but no Retry; the fix is picking a different file.
        error: rejection,
        canRetry: false,
      };
    });
    setPendingFiles((existing) => [...existing, ...rows]);

    // Uploads are independent, so they run concurrently; one slow file must
    // not stall the rest. Each success refetches the list itself, so finished
    // files appear as they land instead of after the whole batch.
    await Promise.all(rows.filter((row) => row.status === "queued").map(uploadRow));
  };

  const handleRetry = (row: PendingPackageFile) => uploadRow(row);

  const handleOpenChange = (next: boolean) => {
    // Rejected and failed rows are feedback about one picking session, not
    // durable state — a reopened modal starts clean. In-flight uploads keep
    // their rows so a completed upload can still clear itself.
    if (!next) {
      setPendingFiles((rows) => rows.filter((row) => row.status === "uploading"));
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={step === "plan" ? "sm:max-w-2xl" : "sm:max-w-lg"}>
        <DialogHeader>
          <DialogTitle>AI Partner planning</DialogTitle>
          <DialogDescription>
            {step === "package" ? (
              <>
                Upload the opportunity package for {opportunityTitle}. AI Partner reads these files
                to draft the plan — file names and contents are the context, no labels needed.
              </>
            ) : (
              <>
                Review the drafted plan for {opportunityTitle} like a diff: edit, reorder, remove,
                or add anything before it becomes real work.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {step === "plan" ? (
          <PlanStep
            latestRun={latestRun}
            parkedRun={parkedRun}
            contextProposal={latestCompleted?.proposal ?? null}
            loading={planRunsPending}
            drafting={drafting}
            draftError={draftError}
            answersPending={answersPending}
            answersError={answersError}
            answerTexts={answerTexts}
            onAnswerTextChange={(index, value) =>
              setAnswerTexts((texts) => ({ ...texts, [index]: value }))
            }
            reviewPlan={reviewPlan}
            onPlanChange={setReviewPlan}
            collaborators={collaborators}
            collaboratorsLoading={collaboratorsLoading}
            onDraft={() => void startDraft()}
            onAnswers={(runId, answers) => void sendAnswers(runId, answers)}
            canContinueWithFiles={filesAddedSincePark}
            onContinueWithFiles={(runId) => void continueWithFiles(runId)}
          />
        ) : (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="grant-package-files">Add package files</Label>
            <Input
              id="grant-package-files"
              ref={fileInputRef}
              type="file"
              multiple
              accept={PACKAGE_FILE_ACCEPT}
              onChange={(event) => void handleFilesSelected(event.target.files)}
            />
            <p className="text-xs text-muted-foreground">
              PDF, DOCX, XLSX, CSV, TXT, or Markdown, up to 50 MB each.
            </p>
          </div>

          {packageFiles.length > 0 || pendingFiles.length > 0 ? (
            <ul className="max-h-64 space-y-1 overflow-y-auto" aria-label="Package files">
              {packageFiles.map((link) => (
                <ServerFileRow
                  key={`link-${link.id}`}
                  link={link}
                  onRetry={() => retryServerFile(link)}
                />
              ))}
              {pendingFiles.map((row) => (
                <PendingFileRow key={`pending-${row.id}`} row={row} onRetry={() => void handleRetry(row)} />
              ))}
            </ul>
          ) : (
            <p className="rounded-md border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
              No package files yet.
            </p>
          )}
        </div>
        )}

        <DialogFooter>
          {step === "package" ? (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Close
              </Button>
              <Button disabled={readyFileCount === 0} onClick={() => setStep("plan")}>
                <Sparkles className="mr-2 h-3.5 w-3.5" aria-hidden />
                Continue to plan
              </Button>
            </>
          ) : (
            <>
              {confirmError ? (
                <p className="flex items-center gap-2 self-center text-sm text-destructive sm:mr-auto">
                  <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                  {confirmError}
                </p>
              ) : null}
              <Button variant="outline" onClick={() => setStep("package")}>
                Back to files
              </Button>
              {/* Enabled once the review holds at least one item: a confirm
                  that would create nothing is the backend's EmptyPlan error,
                  and the button must not offer it. Held off while a redraft
                  is in flight too — the completing run replaces the review
                  copy, so confirming mid-redraft would persist a plan the
                  user has already asked to supersede. */}
              <Button
                disabled={
                  confirmPending ||
                  drafting ||
                  !reviewPlan ||
                  reviewPlan.milestones.length +
                    reviewPlan.requiredDocuments.length +
                    reviewPlan.requirements.length ===
                    0
                }
                onClick={() => void confirmReviewedPlan()}
              >
                {confirmPending ? (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : null}
                Confirm plan
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// The plan step: run state for the drafting agent, then the review editor
// once a draft exists. Failures show the run's own reason with a re-run;
// a re-run replaces the review draft when it completes. A run parked on
// plan-mode questions (ENG-1931) shows those questions with answer inputs
// in place of the empty state.

// The status zone and the empty-state gate both derive from this one
// discriminated phase, so a future run state added to one cannot forget the
// other and render its panel alongside the empty state's Draft button
// (which would offer a double-enqueue).
type PlanPhase =
  | { kind: "loading" }
  | { kind: "drafting" }
  | { kind: "failed"; message: string }
  | { kind: "questions"; run: PlanProposalRun }
  | { kind: "idle" };

function PlanStep({
  latestRun,
  parkedRun,
  contextProposal,
  loading,
  drafting,
  draftError,
  answersPending,
  answersError,
  answerTexts,
  onAnswerTextChange,
  reviewPlan,
  onPlanChange,
  collaborators,
  collaboratorsLoading,
  onDraft,
  onAnswers,
  canContinueWithFiles,
  onContinueWithFiles,
}: {
  latestRun: PlanProposalRun | undefined;
  parkedRun: PlanProposalRun | null;
  contextProposal: PlanProposal | null;
  loading: boolean;
  drafting: boolean;
  draftError: string | null;
  answersPending: boolean;
  answersError: string | null;
  answerTexts: Record<number, string>;
  onAnswerTextChange: (index: number, value: string) => void;
  reviewPlan: ReviewPlan | null;
  onPlanChange: (plan: ReviewPlan) => void;
  collaborators: MunicipalityCollaborator[];
  collaboratorsLoading: boolean;
  onDraft: () => void;
  onAnswers: (runId: number, answers: PlanAnswer[]) => void;
  canContinueWithFiles: boolean;
  onContinueWithFiles: (runId: number) => void;
}) {
  // The enqueue error and the run's own failure share one surface; the
  // enqueue error wins because it is the more recent action. (A stale
  // enqueue error can't hide a parked run's questions: the modal clears it
  // when a new parked run arrives.)
  const failureMessage =
    draftError ?? (latestRun?.status === "failed" ? (latestRun.error_message ?? "The plan draft failed.") : null);
  const phase: PlanPhase =
    // While the run list is still loading there is nothing to say yet: the
    // empty state's Draft button would enqueue a brand-new agent run on top
    // of a draft that exists but has not arrived.
    loading && !reviewPlan
      ? { kind: "loading" }
      : drafting
        ? { kind: "drafting" }
        : failureMessage
          ? { kind: "failed", message: failureMessage }
          : parkedRun
            ? { kind: "questions", run: parkedRun }
            : { kind: "idle" };
  if (phase.kind === "loading") {
    return (
      <div className="flex items-center gap-2 rounded-md border px-3 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading plan drafts…
      </div>
    );
  }
  // Both zones switch exhaustively (per AGENTS.md): a new PlanPhase kind
  // fails to compile here instead of silently rendering a blank plan step.
  const statusZone = (() => {
    switch (phase.kind) {
      case "drafting":
        return (
          <div className="flex items-center gap-2 rounded-md border px-3 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Drafting a plan from the package. This can take a minute or two.
          </div>
        );
      case "failed":
        return (
          <div className="space-y-2 rounded-md border border-destructive/50 px-3 py-4 text-sm">
            <p className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
              {phase.message}
            </p>
            <Button variant="outline" size="sm" onClick={onDraft}>
              <RotateCw className="mr-1 h-3 w-3" aria-hidden />
              Try again
            </Button>
          </div>
        );
      case "questions":
        return (
          <PlanQuestionsPanel
            run={phase.run}
            pending={answersPending}
            error={answersError}
            answerTexts={answerTexts}
            onAnswerTextChange={onAnswerTextChange}
            onAnswers={onAnswers}
            canContinueWithFiles={canContinueWithFiles}
            onContinueWithFiles={onContinueWithFiles}
          />
        );
      case "idle":
        return null;
      default: {
        const _exhaustive: never = phase;
        return _exhaustive;
      }
    }
  })();
  const emptyState = (() => {
    switch (phase.kind) {
      case "idle":
        return (
          <div className="space-y-3 rounded-md border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
            <p>No plan drafted yet. AI Partner will read the processed package and propose one.</p>
            <Button onClick={onDraft}>
              <Sparkles className="mr-2 h-3.5 w-3.5" aria-hidden />
              Draft plan
            </Button>
          </div>
        );
      case "drafting":
      case "failed":
      case "questions":
        return null;
      default: {
        const _exhaustive: never = phase;
        return _exhaustive;
      }
    }
  })();
  return (
    <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
      {statusZone}

      {reviewPlan ? (
        <>
          <GrantPlanReviewEditor
            plan={reviewPlan}
            onPlanChange={onPlanChange}
            collaborators={collaborators}
            collaboratorsLoading={collaboratorsLoading}
          />
          {/* Context belongs to the run under review (the latest completed
              one), not planRuns[0]: a failed re-run must not blank it. */}
          {contextProposal ? <PlanContextSections proposal={contextProposal} /> : null}
          {phase.kind !== "drafting" ? (
            <Button variant="ghost" size="sm" onClick={onDraft}>
              <Sparkles className="mr-1 h-3 w-3" aria-hidden />
              Draft a new plan
            </Button>
          ) : null}
        </>
      ) : (
        emptyState
      )}
    </div>
  );
}

// The server truncates each answer at this many characters (the answers
// controller's ANSWER_MAX_LENGTH); capping the input here keeps the agent
// from silently planning off a mid-sentence fragment of a longer paste.
const ANSWER_MAX_LENGTH = 2000;

// Answer inputs for a parked run's plan-mode questions (ENG-1931). Answer
// text is owned by the modal so it survives this panel unmounting (Back to
// files, a drafting spinner); the modal starts it blank again when a
// continuation parks with a new round of questions, so an earlier round's
// text never submits against different questions. (Earlier rounds' answers
// carry forward server-side in the continuation context.)
function PlanQuestionsPanel({
  run,
  pending,
  error,
  answerTexts,
  onAnswerTextChange,
  onAnswers,
  canContinueWithFiles,
  onContinueWithFiles,
}: {
  run: PlanProposalRun;
  pending: boolean;
  error: string | null;
  answerTexts: Record<number, string>;
  onAnswerTextChange: (index: number, value: string) => void;
  onAnswers: (runId: number, answers: PlanAnswer[]) => void;
  canContinueWithFiles: boolean;
  onContinueWithFiles: (runId: number) => void;
}) {
  const questions = run.questions ?? [];
  const answers: PlanAnswer[] = questions
    .map((question, index) => ({
      question: question.question,
      answer: (answerTexts[index] ?? "").trim(),
    }))
    .filter((entry) => entry.answer !== "");

  return (
    <div className="space-y-3 rounded-md border px-3 py-4 text-sm">
      <p className="font-medium">AI Partner needs a few answers to finish the plan.</p>
      <p className="text-xs text-muted-foreground">
        Answer what you can — or add files from Back to files — and the agent will continue the
        draft with that context.
      </p>
      {questions.map((question, index) => (
        <div key={`${run.id}-${index}`} className="space-y-1">
          <Label htmlFor={`plan-answer-${run.id}-${index}`}>{question.question}</Label>
          {question.reason ? (
            <p className="text-xs italic text-muted-foreground">{question.reason}</p>
          ) : null}
          <Textarea
            id={`plan-answer-${run.id}-${index}`}
            rows={2}
            maxLength={ANSWER_MAX_LENGTH}
            value={answerTexts[index] ?? ""}
            onChange={(event) => onAnswerTextChange(index, event.target.value)}
          />
          {(answerTexts[index] ?? "").length >= ANSWER_MAX_LENGTH ? (
            <p className="text-xs text-muted-foreground">
              Answers are limited to {ANSWER_MAX_LENGTH.toLocaleString()} characters — anything
              longer was trimmed.
            </p>
          ) : null}
        </div>
      ))}
      {error ? (
        <p className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
          {error}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button disabled={pending || answers.length === 0} onClick={() => onAnswers(run.id, answers)}>
          {pending ? (
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <Sparkles className="mr-2 h-3.5 w-3.5" aria-hidden />
          )}
          Send answers
        </Button>
        {/* The add-files path the copy above offers (files_only
            continuation): gated on a file that finished parsing after the
            run parked, so it cannot enqueue an agent run with nothing new
            to read. */}
        <Button
          variant="outline"
          disabled={pending || !canContinueWithFiles}
          title={
            canContinueWithFiles ? undefined : "Add a package file from Back to files first"
          }
          onClick={() => onContinueWithFiles(run.id)}
        >
          <FileText className="mr-2 h-3.5 w-3.5" aria-hidden />
          Continue with the new files
        </Button>
      </div>
    </div>
  );
}

// The error message gets its own wrapped line under the name: inline it can't
// shrink (flex min-width:auto), which forced rows wider than the dialog and
// truncated the one thing the user needs to read — the rejection reason.
function ServerFileRow({
  link,
  onRetry,
}: {
  link: GrantOpportunityDocumentLink;
  onRetry: () => Promise<void>;
}) {
  // Disabled until the retry round-trip settles: a second click before the
  // refetch lands would enqueue (and pay for) a second parse of this file.
  const [retryPending, setRetryPending] = useState(false);
  const failed = link.processing_status === "failed";
  const busy = ["queued", "uploading", "parsing", "retrying"].includes(link.processing_status);
  return (
    <li className="rounded-md border px-3 py-2 text-sm">
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{link.name}</span>
        <StatusChip
          label={SERVER_STATUS_LABELS[link.processing_status]}
          tone={failed ? "error" : link.processing_status === "ready" ? "success" : "busy"}
          spinning={busy}
        />
        {failed ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            disabled={retryPending}
            onClick={() => {
              setRetryPending(true);
              void onRetry().finally(() => setRetryPending(false));
            }}
          >
            <RotateCw className="mr-1 h-3 w-3" aria-hidden />
            Retry
          </Button>
        ) : null}
      </div>
      {failed && link.error_message ? (
        <p className="mt-1 pl-6 text-xs text-destructive">{link.error_message}</p>
      ) : null}
    </li>
  );
}

function PendingFileRow({
  row,
  onRetry,
}: {
  row: PendingPackageFile;
  onRetry: () => void;
}) {
  return (
    <li className="rounded-md border px-3 py-2 text-sm">
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{row.file.name}</span>
        {row.status === "failed" ? (
          <>
            <StatusChip label="Failed" tone="error" />
            {row.canRetry ? (
              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={onRetry}>
                <RotateCw className="mr-1 h-3 w-3" aria-hidden />
                Retry
              </Button>
            ) : null}
          </>
        ) : (
          <StatusChip
            label={row.status === "uploading" ? "Uploading" : "Queued"}
            tone="busy"
            spinning={row.status === "uploading"}
          />
        )}
      </div>
      {row.error ? <p className="mt-1 pl-6 text-xs text-destructive">{row.error}</p> : null}
    </li>
  );
}

function StatusChip({
  label,
  tone,
  spinning = false,
}: {
  label: string;
  tone: "success" | "error" | "busy";
  spinning?: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        tone === "success" && "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
        tone === "error" && "bg-destructive/10 text-destructive",
        tone === "busy" && "bg-muted text-muted-foreground",
      )}
    >
      {spinning ? (
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
      ) : tone === "success" ? (
        <CheckCircle2 className="h-3 w-3" aria-hidden />
      ) : tone === "error" ? (
        <AlertCircle className="h-3 w-3" aria-hidden />
      ) : null}
      {label}
    </span>
  );
}
